import { loadStripe } from "@stripe/stripe-js"
import { createRoot } from "react-dom/client"
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
} from "react"

type CreditsPack = {
  credits: number
  price: string
}

type CreditsMountDataset = {
  organizationName: string
  currentCredits: number
  poweredByStripeBadgeUrl: string
  purchaseUrl: string
  stripePublishableKey: string
  creditPacks: CreditsPack[]
}

type PurchaseIntentResponse = {
  success?: boolean
  error?: string
  already_completed?: boolean
  purchase_completed?: boolean
  setup_intent_client_secret?: string
  setup_intent_status?: string
  organization?: { credits?: number }
  purchase?: { credits?: number; amount_in_cents?: number; setup_intent_id?: string; payment_intent_id?: string }
}

const creditsPacks: CreditsPack[] = [
  { credits: 100, price: "$4.99" },
  { credits: 250, price: "$9.99" },
  { credits: 600, price: "$19.99" },
  { credits: 1500, price: "$49.99" },
  { credits: 3500, price: "$99.99" },
]

function parseMountDataset(el: HTMLElement): CreditsMountDataset {
  const packsRaw = el.dataset.creditPacks ?? "[]"
  let creditPacks: CreditsPack[] = creditsPacks

  try {
    const parsed = JSON.parse(packsRaw) as Array<{ credits?: number; amount_in_cents?: number; price?: string }>
    creditPacks = parsed
      .map((pack) => ({
        credits: Number(pack.credits),
        price: typeof pack.price === "string"
          ? pack.price
          : `$${((Number(pack.amount_in_cents) || 0) / 100).toFixed(2)}`,
      }))
      .filter((pack) => Number.isFinite(pack.credits) && pack.credits > 0)
  } catch {
    creditPacks = creditsPacks
  }

  return {
    organizationName: el.dataset.organizationName ?? "Your organization",
    currentCredits: Number.parseInt(el.dataset.currentCredits ?? "0", 10) || 0,
    poweredByStripeBadgeUrl: el.dataset.poweredByStripeBadgeUrl ?? "",
    purchaseUrl: el.dataset.purchaseUrl ?? "",
    stripePublishableKey: el.dataset.stripePublishableKey ?? "",
    creditPacks,
  }
}

function CreditsApp({
  organizationName,
  currentCredits,
  poweredByStripeBadgeUrl,
  purchaseUrl,
  stripePublishableKey,
  creditPacks,
}: CreditsMountDataset): ReactElement {
  const [open, setOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(2)
  const [status, setStatus] = useState<string | null>(null)
  const [balance, setBalance] = useState(currentCredits)
  const [idempotencyKey, setIdempotencyKey] = useState(() => generateIdempotencyKey())
  const [isLoadingIntent, setIsLoadingIntent] = useState(false)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [setupIntentStatus, setSetupIntentStatus] = useState<string | null>(null)
  const [isCompletingPurchase, setIsCompletingPurchase] = useState(false)
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)
  const paymentElementContainerRef = useRef<HTMLDivElement | null>(null)
  const stripeRef = useRef<any>(null)
  const elementsRef = useRef<any>(null)
  const paymentElementRef = useRef<any>(null)

  const selectedPack = useMemo(() => creditPacks[selectedIndex] ?? creditPacks[0], [selectedIndex, creditPacks])
  const stripePromise = useMemo(() => {
    if (!stripePublishableKey) return null
    return loadStripe(stripePublishableKey)
  }, [stripePublishableKey])

  useEffect(() => {
    if (!open) return

    const handleKeydown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setOpen(false)
      }
    }

    document.addEventListener("keydown", handleKeydown)
    document.body.style.overflow = "hidden"
    requestAnimationFrame(() => {
      closeButtonRef.current?.focus()
    })

    return () => {
      document.removeEventListener("keydown", handleKeydown)
      document.body.style.overflow = ""
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    let cancelled = false

    async function createIntent(): Promise<void> {
      setIsLoadingIntent(true)
      setClientSecret(null)
      setSetupIntentStatus(null)
      cleanupPaymentElement()

      if (!purchaseUrl) {
        setStatus("Purchase endpoint is not configured.")
        setIsLoadingIntent(false)
        return
      }

      const csrfToken = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content
      if (!csrfToken) {
        setStatus("Missing CSRF token.")
        setIsLoadingIntent(false)
        return
      }

      try {
        const response = await fetch(purchaseUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
            "Idempotency-Key": idempotencyKey,
          },
          body: JSON.stringify({
            purchase: {
              credits: selectedPack.credits,
              browser_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? "",
              browser_locale: navigator.language ?? "",
            },
          }),
        })

        const payload = await response.json() as PurchaseIntentResponse
        if (!response.ok || !payload.success) {
          throw new Error(payload.error || "Purchase failed.")
        }

        if (payload.already_completed) {
          if (typeof payload.organization?.credits === "number") {
            setBalance(payload.organization.credits)
          }
          setOpen(false)
          setStatus("That purchase is already complete. Your balance is updated.")
          return
        }

        if (!payload.setup_intent_client_secret) {
          throw new Error("Stripe did not return a payment form.")
        }

        if (cancelled) return

        setClientSecret(payload.setup_intent_client_secret)
        setSetupIntentStatus(payload.setup_intent_status ?? null)
        setStatus(null)
      } catch (error) {
        if (cancelled) return
        setStatus(error instanceof Error ? error.message : "Purchase failed.")
      } finally {
        if (!cancelled) setIsLoadingIntent(false)
      }
    }

    void createIntent()

    return () => {
      cancelled = true
    }
  }, [open, selectedPack.credits, idempotencyKey, purchaseUrl])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const setupIntentId = params.get("setup_intent")
    if (!setupIntentId) return

    void completePurchase(setupIntentId)
  }, [])

  useEffect(() => {
    if (!open || !clientSecret || !stripePromise || !paymentElementContainerRef.current) return

    const paymentClientSecret = clientSecret

    let cancelled = false

    async function mountPaymentElement(): Promise<void> {
      const stripe = await stripePromise
      if (cancelled || !stripe || !paymentElementContainerRef.current) return

      const elements = stripe.elements({
        clientSecret: paymentClientSecret,
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: "#155a96",
            colorBackground: "#ffffff",
            colorText: "#0f172a",
          },
        },
      })

      const paymentElement = elements.create("payment", {
        layout: {
          type: "tabs",
          defaultCollapsed: false,
        },
      })

      stripeRef.current = stripe
      elementsRef.current = elements
      paymentElementRef.current = paymentElement
      paymentElement.mount(paymentElementContainerRef.current)
    }

    void mountPaymentElement().catch((error: unknown) => {
      setStatus(error instanceof Error ? error.message : "Unable to load Stripe payment form.")
    })

    return () => {
      cancelled = true
      cleanupPaymentElement()
    }
  }, [open, clientSecret, stripePromise])

  function cleanupPaymentElement(): void {
    if (paymentElementRef.current) {
      try {
        paymentElementRef.current.destroy?.()
      } catch {
        // Ignore cleanup errors from Stripe's iframe lifecycle.
      }
    }

    paymentElementRef.current = null
    elementsRef.current = null
    stripeRef.current = null

    if (paymentElementContainerRef.current) {
      paymentElementContainerRef.current.innerHTML = ""
    }
  }

  function openModal(): void {
    setStatus(null)
    setIdempotencyKey(generateIdempotencyKey())
    setOpen(true)
  }

  function closeModal(): void {
    setOpen(false)
    cleanupPaymentElement()
  }

  function selectPack(index: number): void {
    setSelectedIndex(index)
    setIdempotencyKey(generateIdempotencyKey())
  }

  async function completePurchase(setupIntentId: string): Promise<void> {
    setIsCompletingPurchase(true)

    try {
      const csrfToken = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content
      if (!csrfToken) {
        throw new Error("Missing CSRF token.")
      }

      const response = await fetch(purchaseUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Idempotency-Key": idempotencyKey,
        },
        body: JSON.stringify({
          purchase: {
            setup_intent_id: setupIntentId,
          },
        }),
      })

      const payload = await response.json() as PurchaseIntentResponse
      if (!response.ok || !payload.success) {
        throw new Error(payload.error || "Purchase finalization failed.")
      }

      if (typeof payload.organization?.credits === "number") {
        setBalance(payload.organization.credits)
      }

      setOpen(false)
      cleanupPaymentElement()
      setStatus(`Purchase complete. Your balance is updated.`)
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Purchase finalization failed.")
    } finally {
      setIsCompletingPurchase(false)
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setStatus(null)

    if (!stripeRef.current || !elementsRef.current) {
      setStatus("Payment form is still loading.")
      return
    }

    try {
      const returnUrl = new URL(window.location.href)
      returnUrl.searchParams.delete("purchase")
      returnUrl.searchParams.delete("setup_intent")
      returnUrl.searchParams.delete("setup_intent_client_secret")
      returnUrl.searchParams.delete("redirect_status")

      const result = await stripeRef.current.confirmSetup({
        elements: elementsRef.current,
        confirmParams: {
          return_url: returnUrl.toString(),
        },
        redirect: "if_required",
      })

      if (result.error) {
        throw new Error(result.error.message || "Payment failed.")
      }

      const setupIntentId = result.setupIntent?.id
      if (!setupIntentId) {
        setStatus("Card saved. Finishing purchase...")
        return
      }

      await completePurchase(setupIntentId)
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Payment failed.")
    }
  }

  return (
    <div className="credits-widget">
      <div className="credits-widget__summary">
        <div>
          <p className="credits-widget__eyebrow">Balance</p>
          <p className="credits-widget__count">{balance.toLocaleString()} credits</p>
          <p className="credits-widget__hint">
            Credits are needed for AI paper review.
          </p>
        </div>

        <button type="button" className="btn btn--primary credits-widget__cta" onClick={openModal}>
          Buy credits
        </button>
      </div>

      {status ? <p className="credits-widget__status" role="status">{status}</p> : null}

      <div className={`credits-modal ${open ? "is-open" : ""}`} aria-hidden={!open}>
        <div className="credits-modal__backdrop" onClick={closeModal} />
        <div className="credits-modal__panel" role="dialog" aria-modal="true" aria-labelledby="credits-modal-title">
          <button
            ref={closeButtonRef}
            type="button"
            className="credits-modal__close"
            aria-label="Close purchase dialog"
            onClick={closeModal}
          >
            ×
          </button>

          <p className="credits-modal__eyebrow">Purchase credits</p>
          <h2 id="credits-modal-title" className="credits-modal__title">
            Purchase more credits for {organizationName}
          </h2>
          <p className="credits-modal__description">
            Choose a credit pack. Stripe will save the card to this organization and then charge the purchase.
          </p>

          <form className="credits-modal__form" onSubmit={handleSubmit}>
            <label className="credits-modal__field">
              <span className="credits-modal__label">Credits to add</span>
              <select
                className="credits-modal__select"
                value={selectedIndex}
                onChange={(event) => selectPack(Number.parseInt(event.target.value, 10))}
                disabled={isLoadingIntent}
              >
                {creditPacks.map((pack, index) => (
                  <option key={pack.credits} value={index}>
                    {pack.credits.toLocaleString()} credits for {pack.price}
                  </option>
                ))}
              </select>
            </label>

            <div className="credits-modal__summary">
              <div>
                <span className="credits-modal__summary-label">Selected pack</span>
                <strong>{selectedPack.credits.toLocaleString()} credits</strong>
              </div>
              <div>
                <span className="credits-modal__summary-label">Price</span>
                <strong>{selectedPack.price}</strong>
              </div>
            </div>

            <div className="credits-modal__stripe">
              {isLoadingIntent ? (
                <div className="credits-modal__loading">
                  Loading secure payment form...
                </div>
              ) : null}
              <div ref={paymentElementContainerRef} className="credits-modal__payment-element" />
            </div>

            <div className="credits-modal__footer">
              {poweredByStripeBadgeUrl ? (
                <img
                  className="credits-modal__stripe-badge"
                  src={poweredByStripeBadgeUrl}
                  alt="Powered by Stripe"
                  height={26}
                />
              ) : null}
              <button type="submit" className="btn btn--primary" disabled={isLoadingIntent || isCompletingPurchase || !clientSecret}>
                {setupIntentStatus === "succeeded" ? "Save card again" : "Save card and buy"}
              </button>
            </div>
          </form>

          <p className="credits-modal__balance">
            Current balance: {balance.toLocaleString()} credits
          </p>
        </div>
      </div>
    </div>
  )
}

function mountCreditsApp(): void {
  const el = document.getElementById("organization-credits-app")
  if (!el) return

  const props = parseMountDataset(el)
  createRoot(el).render(<CreditsApp {...props} />)
}

function generateIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }

  return `local_${Math.random().toString(36).slice(2)}_${Date.now()}`
}

mountCreditsApp()
